/**
 * Replays a RunRecording through the real simulation, headless, and keeps
 * the state of every tick: the cached trajectory that ghosts, the replay
 * viewer (exact seeking: every tick's state is stored, nothing is
 * re-simulated or estimated) and run analytics all read.
 *
 * The replay simulation shares the track's LoadedLevel, whose physics world
 * is static and only ever queried, so no second physics world is built. It
 * gets its own MemoryRecordStore: a replay can never write a record.
 *
 * Verification compares the replay's state hashes with the recording's
 * (every HASH_INTERVAL ticks and at the end) and its finish time and splits.
 * "verified" means this build's simulation reproduced the recorded run
 * exactly; it says nothing about how the recording was made (docs/REPLAYS.md).
 */
import type { EngineConfig } from '../core/Config';
import { MemoryRecordStore } from '../gameplay/RecordStore';
import type { InputFrame } from '../input/InputFrame';
import { makeInput } from '../input/InputFrame';
import type { MovementRuleSet } from '../movement/MovementRules';
import { Simulation } from '../simulation/Simulation';
import type { LoadedLevel } from '../world/LevelLoader';
import { stateHash } from '../devtools/Recorder';
import { frameAt } from './InputTape';
import { compatibility, HASH_INTERVAL, type Compatibility, type RunRecording } from './RecordingFormat';
import type { Split } from './RunTiming';

export type TrajectoryEventKind = 'start' | 'jump' | 'land' | 'death' | 'respawn' | 'checkpoint' | 'finish';

export interface TrajectoryEvent {
  /** Sample index (state after `sample` ticks). */
  sample: number;
  kind: TrajectoryEventKind;
  /** Checkpoint index (checkpoint events). */
  index?: number;
}

/** Per-sample flag bits. */
export const F_GROUNDED = 1;
export const F_ALIVE = 2;
/** In contact with a surface that is not ground (wall, ceiling, steep slope). */
export const F_WALL = 4;
/** The player was placed (respawn/restart): do not interpolate into this sample. */
export const F_TELEPORT = 8;
/** Crouched or sliding (the camera is lower). */
export const F_CROUCHED = 16;

export type VerifyStatus = 'verified' | 'diverged' | 'incompatible';

export interface Verification {
  status: VerifyStatus;
  /** Incompatibility reasons, or where the divergence was detected. */
  reasons: string[];
  /** First hash checkpoint (tick) at which the replay differed, if any. */
  divergedAtTick: number | null;
}

export interface Trajectory {
  /** Samples: index j is the state after j ticks (0 = initial placement). */
  readonly samples: number;
  readonly dt: number;
  readonly px: Float64Array;
  readonly py: Float64Array;
  readonly pz: Float64Array;
  readonly vx: Float64Array;
  readonly vy: Float64Array;
  readonly vz: Float64Array;
  readonly yaw: Float64Array;
  readonly pitch: Float64Array;
  readonly flags: Uint8Array;
  readonly events: TrajectoryEvent[];
  /** Simulation time (s from the first sample) at which the timed attempt started; null if it never did. */
  readonly startTime: number | null;
  /** Finish run time as the replay measured it, null without a finish. */
  readonly finishTime: number | null;
  readonly splits: Split[];
  readonly verification: Verification;
}

export interface ReplayContext {
  level: LoadedLevel;
  config: Readonly<EngineConfig>;
  rules: MovementRuleSet;
  /** The game's respawn delay, when the replay must match the game (ghosts, the viewer). */
  respawnDelayTicks?: number;
}

function sameSplits(a: readonly Split[], b: readonly Split[]): boolean {
  return a.length === b.length && a.every((s, i) => s.index === b[i]!.index && s.time === b[i]!.time);
}

/** Run time (s) at sample `j` (negative before the start). */
export function runTimeAt(t: Pick<Trajectory, 'dt' | 'startTime'>, j: number): number {
  return j * t.dt - (t.startTime ?? 0);
}

/** Fractional sample index at run time `seconds`, clamped to the trajectory. */
export function sampleAt(t: Pick<Trajectory, 'dt' | 'startTime' | 'samples'>, seconds: number): number {
  const j = (seconds + (t.startTime ?? 0)) / t.dt;
  return Math.min(Math.max(j, 0), t.samples - 1);
}

/** Interpolated position at a fractional sample (teleports are not interpolated across). */
export function positionAt(t: Trajectory, j: number, out: { x: number; y: number; z: number }): { x: number; y: number; z: number } {
  const a = Math.floor(j);
  const b = Math.min(a + 1, t.samples - 1);
  const f = (t.flags[b]! & F_TELEPORT) !== 0 ? 0 : j - a;
  out.x = t.px[a]! + (t.px[b]! - t.px[a]!) * f;
  out.y = t.py[a]! + (t.py[b]! - t.py[a]!) * f;
  out.z = t.pz[a]! + (t.pz[b]! - t.pz[a]!) * f;
  return out;
}

export interface SimulateOptions {
  /** Ticks per slice before yielding to the event loop (keeps the page responsive). */
  slice?: number;
  signal?: AbortSignal;
  onProgress?: (fraction: number) => void;
}

const WALL_NORMAL_DOT = 0.7;

/**
 * Re-simulates a recording. An incompatible recording (other track version,
 * rules or simulation) is still simulated so it can be looked at, but its
 * verification is "incompatible" and nothing should present it as the run
 * it claims to be.
 */
export async function simulateTrajectory(rec: RunRecording, ctx: ReplayContext, opts: SimulateOptions = {}): Promise<Trajectory> {
  const sim = new Simulation(ctx.level, ctx.config, { rules: ctx.rules, records: new MemoryRecordStore(), respawnDelayTicks: rec.config.respawnDelayTicks });
  const compat: Compatibility = compatibility(rec, { recordKey: sim.recordKey(), tickRate: ctx.config.simulation.tickRate, ...(ctx.respawnDelayTicks === undefined ? {} : { respawnDelayTicks: ctx.respawnDelayTicks }) });
  sim.setMode(rec.start.mode);
  const s = rec.start.spawn;
  const st0 = ctx.level.data.start;
  if (s.position.some((c, i) => c !== st0.position[i]) || s.yawDeg !== st0.yawDeg) sim.placeAtSpawn(s);

  const n = rec.ticks + 1;
  const t = {
    samples: n,
    dt: sim.dt,
    px: new Float64Array(n),
    py: new Float64Array(n),
    pz: new Float64Array(n),
    vx: new Float64Array(n),
    vy: new Float64Array(n),
    vz: new Float64Array(n),
    yaw: new Float64Array(n),
    pitch: new Float64Array(n),
    flags: new Uint8Array(n),
  };
  const events: TrajectoryEvent[] = [];
  let startTime: number | null = null;
  let finishTime: number | null = null;
  let splits: Split[] = [];
  let sample = 0;
  let placed = false;
  const off = [
    sim.events.on('attempt-start', (e) => {
      startTime = e.time;
      events.push({ sample: sample + 1, kind: 'start' });
    }),
    sim.events.on('attempt-reset', () => {
      if (finishTime === null) {
        startTime = null;
        splits = [];
      }
    }),
    sim.events.on('checkpoint', (e) => {
      splits.push({ index: e.index, time: e.split });
      events.push({ sample: sample + 1, kind: 'checkpoint', index: e.index });
    }),
    sim.events.on('finish', (e) => {
      if (finishTime === null) finishTime = e.seconds;
      events.push({ sample: sample + 1, kind: 'finish' });
    }),
    sim.events.on('death', () => events.push({ sample: sample + 1, kind: 'death' })),
    sim.events.on('respawn', () => {
      placed = true;
      events.push({ sample: sample + 1, kind: 'respawn' });
    }),
  ];

  const write = (j: number, pitch: number): void => {
    const p = sim.state.position;
    const v = sim.state.velocity;
    t.px[j] = p.x;
    t.py[j] = p.y;
    t.pz[j] = p.z;
    t.vx[j] = v.x;
    t.vy[j] = v.y;
    t.vz[j] = v.z;
    t.yaw[j] = sim.state.yaw;
    t.pitch[j] = pitch;
    let f = (sim.state.grounded ? F_GROUNDED : 0) | (sim.state.alive ? F_ALIVE : 0) | (placed ? F_TELEPORT : 0) | (sim.state.crouched ? F_CROUCHED : 0);
    const up = sim.state.up;
    for (const c of sim.state.contacts) if (c.normal.x * up.x + c.normal.y * up.y + c.normal.z * up.z < WALL_NORMAL_DOT) f |= F_WALL;
    t.flags[j] = f;
    placed = false;
  };

  write(0, rec.inputs.length > 0 ? rec.inputs.pitch[0]! : 0);
  const input: InputFrame = makeInput();
  let cmd = 0;
  let divergedAt: number | null = null;
  let wasGrounded = sim.state.grounded;
  const slice = opts.slice ?? 4000;
  try {
    for (let i = 0; i < rec.ticks; i++) {
      while (cmd < rec.commands.length && rec.commands[cmd]!.tick === i) {
        sim.restart(false);
        cmd++;
      }
      sim.step(frameAt(rec.inputs, i, input));
      sample = i + 1;
      write(sample, input.pitch);
      if (sim.state.alive && sim.state.ticksSinceJump === 1) events.push({ sample, kind: 'jump' });
      else if (sim.state.grounded && !wasGrounded && sim.state.alive) events.push({ sample, kind: 'land' });
      wasGrounded = sim.state.grounded;
      if (sample % HASH_INTERVAL === 0 && divergedAt === null) {
        const h = rec.hashes[sample / HASH_INTERVAL - 1];
        if (h !== undefined && h !== stateHash(sim)) divergedAt = sample;
      }
      if (sample % slice === 0) {
        opts.onProgress?.(sample / rec.ticks);
        await new Promise((r) => setTimeout(r, 0));
        if (opts.signal?.aborted) throw new DOMException('Replay simulation cancelled', 'AbortError');
      }
    }
  } finally {
    for (const o of off) o();
  }
  events.sort((a, b) => a.sample - b.sample);

  const reasons: string[] = [];
  let status: VerifyStatus;
  if (!compat.ok) {
    status = 'incompatible';
    reasons.push(...compat.reasons);
  } else {
    if (divergedAt !== null) reasons.push(`The replay first differs from the recording by tick ${divergedAt}.`);
    else if (stateHash(sim) !== rec.finalHash) reasons.push('The replay ends in a different state than the recording.');
    if (rec.result.outcome === 'finished' && finishTime !== rec.result.seconds) reasons.push('The replay does not reproduce the recorded finish time.');
    else if (rec.result.outcome === 'finished' && !sameSplits(splits, rec.result.splits)) reasons.push('The replay does not reproduce the recorded checkpoint splits.');
    status = reasons.length === 0 ? 'verified' : 'diverged';
  }
  return { ...t, events, startTime, finishTime, splits, verification: { status, reasons, divergedAtTick: divergedAt } };
}
